import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Typography,
  FormControlLabel,
} from '@mui/material';
import type { MessageDefinition } from '@k-ucap/shared';
import { TransferMode } from '@k-ucap/shared';

interface MessageBrowserProps {
  messages: MessageDefinition[];
  selected: MessageDefinition | null;
  onSelect: (msg: MessageDefinition) => void;
  filter: {
    search: string;
    category: string;
    sourceRole: string;
    targetRole: string;
    transferMode: string;
    supportedOnly: boolean;
  };
  onFilterChange: (filter: MessageBrowserProps['filter']) => void;
  categories: string[];
  sourceRoles: string[];
  targetRoles: string[];
}

export const MessageBrowser = ({
  messages,
  selected,
  onSelect,
  filter,
  onFilterChange,
  categories,
  sourceRoles,
  targetRoles,
}: MessageBrowserProps) => {
  const update = (patch: Partial<MessageBrowserProps['filter']>) => {
    onFilterChange({ ...filter, ...patch });
  };

  return (
    <Paper sx={{ height: '100%', p: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography variant="subtitle2">Message Browser</Typography>
      <TextField
        size="small"
        fullWidth
        placeholder="ID, Name, Tag 검색"
        value={filter.search}
        onChange={(e) => update({ search: e.target.value })}
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <FormControl size="small" fullWidth>
          <InputLabel>Category</InputLabel>
          <Select value={filter.category} label="Category" onChange={(e) => update({ category: e.target.value })}>
            <MenuItem value="">전체</MenuItem>
            {categories.map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" fullWidth>
          <InputLabel>Source Role</InputLabel>
          <Select
            value={filter.sourceRole}
            label="Source Role"
            onChange={(e) => update({ sourceRole: e.target.value })}
          >
            <MenuItem value="">전체</MenuItem>
            {sourceRoles.map((r) => (
              <MenuItem key={r} value={r}>
                {r}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" fullWidth>
          <InputLabel>Target Role</InputLabel>
          <Select
            value={filter.targetRole}
            label="Target Role"
            onChange={(e) => update({ targetRole: e.target.value })}
          >
            <MenuItem value="">전체</MenuItem>
            {targetRoles.map((r) => (
              <MenuItem key={r} value={r}>
                {r}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" fullWidth>
          <InputLabel>Transfer Mode</InputLabel>
          <Select
            value={filter.transferMode}
            label="Transfer Mode"
            onChange={(e) => update({ transferMode: e.target.value })}
          >
            <MenuItem value="">전체</MenuItem>
            {Object.values(TransferMode).map((m) => (
              <MenuItem key={m} value={m}>
                {m}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControlLabel
          control={
            <Switch checked={filter.supportedOnly} onChange={(e) => update({ supportedOnly: e.target.checked })} />
          }
          label="Supported Only"
        />
      </Box>
      <List dense sx={{ flexGrow: 1, overflow: 'auto' }}>
        {messages.map((msg) => (
          <ListItem key={msg.id} disablePadding>
            <ListItemButton
              selected={selected?.id === msg.id}
              onClick={() => onSelect(msg)}
              sx={{ flexDirection: 'column', alignItems: 'flex-start' }}
            >
              <ListItemText
                primary={`#${msg.messageType} ${msg.displayName}`}
                secondary={
                  <Box component="span" sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                    <Chip label={msg.category} size="small" />
                    <Chip label={`${msg.sourceRole}→${msg.targetRole}`} size="small" />
                    <Chip label={msg.transferMode} size="small" />
                    {!msg.supported && <Chip label="Unsupported" size="small" color="warning" />}
                  </Box>
                }
                slotProps={{ secondary: { component: 'div' } }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Paper>
  );
};
